import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Loader2, AlertCircle } from "lucide-react";

interface PlaceDetails {
  street: string;
  city: string;
  state: string;
  county: string;
  postalCode: string;
  countryCode: string;
  formattedAddress: string;
  /** Map location of the picked place; null when Places returned no geometry. */
  latitude: number | null;
  longitude: number | null;
}

export type PlacesApiStatus = "loading" | "ready" | "error";

interface GooglePlacesAutocompleteProps {
  apiKey: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect: (placeDetails: PlaceDetails) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  size?: "sm" | "md";
  /** Fired when the field loses focus — after any pending suggestion pick. */
  onBlur?: () => void;
  /**
   * Lets the caller react to Places being unavailable (bad key, blocked script,
   * offline) instead of trapping the user behind a dropdown that never opens.
   */
  onStatusChange?: (status: PlacesApiStatus) => void;
  /** Gives the caller a handle on the input, e.g. to focus it on a failed save. */
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  /**
   * A suggestion was picked but its details could not be fetched, so no
   * coordinates are coming. Lets the caller stop demanding a pick the user has
   * already made.
   */
  onPlaceSelectError?: () => void;
}

interface AutocompletePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

declare global {
  interface Window {
    google: any;
    initGooglePlaces: () => void;
  }
}

export const GooglePlacesAutocomplete: React.FC<
  GooglePlacesAutocompleteProps
> = ({
  apiKey,
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Enter address",
  disabled = false,
  error = false,
  size = "md",
  onBlur,
  onStatusChange,
  inputRef: externalInputRef,
  onPlaceSelectError,
}) => {
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [apiStatus, setApiStatus] = useState<PlacesApiStatus>("loading");

  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const scriptLoadedRef = useRef(false);
  /** Text typed while the script was still loading, searched once it is ready. */
  const pendingQueryRef = useRef<string | null>(null);

  const placesLibRef = useRef<any>(null);

  useEffect(() => {
    if (!apiKey) {
      setApiStatus("error");
      return;
    }

    if (scriptLoadedRef.current) {
      if (!placesLibRef.current && window.google?.maps?.importLibrary) {
        importPlacesLibrary().catch(() => setApiStatus("error"));
      }
      return;
    }
    if (
      window.google &&
      window.google.maps &&
      window.google.maps.importLibrary
    ) {
      importPlacesLibrary().catch(() => setApiStatus("error"));
      scriptLoadedRef.current = true;
      return;
    }
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]',
    );
    if (existingScript) {
      const checkLoaded = () => {
        if (
          window.google &&
          window.google.maps &&
          window.google.maps.importLibrary
        ) {
          importPlacesLibrary().catch(() => setApiStatus("error"));
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
      scriptLoadedRef.current = true;
      return;
    }

    window.initGooglePlaces = () => {
      importPlacesLibrary().catch(() => setApiStatus("error"));
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&libraries=places&v=beta&loading=async&callback=initGooglePlaces`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      setApiStatus("error");
    };

    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  }, [apiKey]);

  const importPlacesLibrary = async () => {
    try {
      const places = await window.google.maps.importLibrary("places");
      if (!places?.AutocompleteSuggestion) {
        throw new Error("Places AutocompleteSuggestion not available");
      }
      placesLibRef.current = places;
      setApiStatus("ready");
    } catch (err) {
      console.error("importPlacesLibrary error:", err);
      setApiStatus("error");
      throw err;
    }
  };

  const parseAddressComponents = (addressComponents: any[]): PlaceDetails => {
    const components: PlaceDetails = {
      street: "",
      city: "",
      state: "",
      county: "",
      postalCode: "",
      countryCode: "",
      formattedAddress: "",
      latitude: null,
      longitude: null,
    };

    addressComponents?.forEach((component: any) => {
      const types = component.types || [];
      const longVal = component.longText || "";
      const shortVal = component.shortText || "";

      if (types.includes("street_number")) {
        components.street = longVal + " " + components.street;
      } else if (types.includes("route")) {
        components.street = components.street + longVal;
      } else if (types.includes("locality")) {
        components.city = longVal;
      } else if (types.includes("administrative_area_level_1")) {
        components.state = longVal;
      } else if (types.includes("administrative_area_level_2")) {
        components.county = longVal;
      } else if (types.includes("postal_code")) {
        components.postalCode = longVal;
      } else if (types.includes("country")) {
        components.countryCode = shortVal;
      }
    });

    components.street = components.street.trim();
    return components;
  };

  const searchPlaces = useCallback(async (query: string) => {
    if (query.trim().length < 1) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      if (
        !placesLibRef.current ||
        !placesLibRef.current.AutocompleteSuggestion
      ) {
        if (window.google?.maps?.importLibrary) {
          await importPlacesLibrary();
        } else {
          throw new Error("Places library not available");
        }
      }

      const request = { input: query, language: "en" };
      const AutocompleteSuggestion =
        placesLibRef.current.AutocompleteSuggestion;
      const response =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const formattedPredictions = (response?.suggestions || [])
        .map((s: any) => {
          const placePred = s.placePrediction || {};
          const mainText =
            placePred.structuredFormat?.mainText?.text ||
            placePred.structured_formatting?.main_text ||
            placePred.text?.text ||
            "";
          const secondaryText =
            placePred.structuredFormat?.secondaryText?.text ||
            placePred.structured_formatting?.secondary_text ||
            "";
          const description =
            placePred.text?.text ||
            placePred.displayText ||
            placePred.description ||
            mainText;
          const placeId = placePred.placeId || placePred.place_id || "";
          return {
            place_id: placeId,
            description,
            structured_formatting: {
              main_text: mainText,
              secondary_text: secondaryText,
            },
          };
        })
        .filter((p: any) => p.description && p.description.trim().length > 0);

      if (formattedPredictions.length === 0) {
        setPredictions([
          {
            place_id: "__no_match__",
            description: "No results found",
            structured_formatting: {
              main_text: "No results found",
              secondary_text: "",
            },
          },
        ]);
        setShowDropdown(true);
      } else {
        setPredictions(formattedPredictions);
        setShowDropdown(true);
      }

      setSelectedIndex(-1);
    } catch (error) {
      console.error("searchPlaces error:", error);
      setPredictions([
        {
          place_id: "__no_match__",
          description: "No results found",
          structured_formatting: {
            main_text: "No results found",
            secondary_text: "",
          },
        },
      ]);
      setShowDropdown(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        searchPlaces(query);
      }, 300);
    },
    [searchPlaces],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (apiStatus === "ready") {
      debouncedSearch(newValue);
    } else if (apiStatus === "loading") {
      // Typing before the script finished loading must not be swallowed —
      // remember it and search as soon as Places is usable.
      pendingQueryRef.current = newValue;
    }
  };

  useEffect(() => {
    onStatusChange?.(apiStatus);
    if (apiStatus !== "ready") return;
    const pending = pendingQueryRef.current;
    pendingQueryRef.current = null;
    // Only chase the leftover query while the user is still in the field —
    // popping a dropdown open under a field they have left would be jarring.
    if (pending && document.activeElement === inputRef.current) {
      searchPlaces(pending);
    }
  }, [apiStatus, onStatusChange, searchPlaces]);

  const fetchPlaceDetails = async (placeId: string) => {
    setIsLoading(true);
    try {
      const place = new window.google.maps.places.Place({ id: placeId });
      const response = await place.fetchFields({
        // `location` is what gives us the lat/lng of the picked place.
        fields: ["addressComponents", "formattedAddress", "location"],
      });

      const placeDetails = parseAddressComponents(
        response?.place?.addressComponents || [],
      );
      placeDetails.formattedAddress = response?.place?.formattedAddress || "";
      placeDetails.street = response?.place?.formattedAddress || "";

      // `location` comes back either as a LatLng instance (lat()/lng() methods)
      // or as a plain LatLngLiteral, depending on the loaded Places version.
      const location = response?.place?.location;
      if (location) {
        const lat =
          typeof location.lat === "function" ? location.lat() : location.lat;
        const lng =
          typeof location.lng === "function" ? location.lng() : location.lng;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          placeDetails.latitude = lat;
          placeDetails.longitude = lng;
        }
      }

      onPlaceSelect(placeDetails);
      setShowDropdown(false);
      setPredictions([]);
    } catch (error) {
      console.error("fetchPlaceDetails error:", error);
      // The pick stands (the text is in the box) but no coordinates are
      // coming — tell the caller so it does not ask for a pick again.
      setShowDropdown(false);
      setPredictions([]);
      onPlaceSelectError?.();
    } finally {
      setIsLoading(false);
    }
  };

  const handlePredictionSelect = (prediction: AutocompletePrediction) => {
    onChange(prediction.description);
    fetchPlaceDetails(prediction.place_id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) {
      // Enter on a hand-typed address would otherwise submit the whole form
      // before any suggestion was ever offered. Show the suggestions instead.
      if (e.key === "Enter") {
        e.preventDefault();
        if (apiStatus === "ready" && value.trim()) searchPlaces(value);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, predictions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0)
          fetchPlaceDetails(predictions[selectedIndex].place_id);
        else if (
          predictions.length === 1 &&
          predictions[0].place_id !== "__no_match__"
        ) {
          fetchPlaceDetails(predictions[0].place_id);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !dropdownRef.current?.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getStatusIcon = () => {
    if (isLoading)
      return <Loader2 className="h-4 w-4 animate-spin text-[#C97A3C]" />;
    if (apiStatus === "ready")
      return <MapPin className="h-4 w-4 text-dark-900" />;
    if (apiStatus === "error")
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    return <Loader2 className="h-4 w-4 animate-spin text-gray-400" />;
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={(el) => {
            inputRef.current = el;
            if (externalInputRef) externalInputRef.current = el;
          }}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Coming back to a half-typed address should offer its suggestions
            // again rather than leave the user staring at a dead field.
            if (apiStatus === "ready" && value.trim() && !showDropdown) {
              debouncedSearch(value);
            }
          }}
          onBlur={() => {
            setShowDropdown(false);
            setSelectedIndex(-1);
            onBlur?.();
          }}
          placeholder={placeholder}
          // Never disabled while the script loads — typing must always work,
          // even if Places never becomes available.
          disabled={disabled}
          className={`
            w-full ${size === "sm" ? "h-8 px-2.5 py-1 text-[13px]" : "px-3 py-2"} pr-10 border rounded-md
            focus:outline-none focus:ring-2 focus:ring-[#C97A3C] focus:border-[#C97A3C]
            disabled:bg-[#F5F4F0] disabled:text-[#8A9590] disabled:cursor-not-allowed
            ${error ? "border-[#E24B4A]" : "border-[rgba(11,15,14,0.15)]"}
          `}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {getStatusIcon()}
        </div>
      </div>

      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          // Keep focus in the input while a suggestion is being clicked —
          // otherwise blur closes the dropdown before the click can land.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {predictions.map((prediction, index) => (
            <div
              key={prediction.place_id}
              onClick={() => {
                if (prediction.place_id !== "__no_match__") {
                  handlePredictionSelect(prediction);
                }
              }}
              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0
                hover:bg-gray-50 transition-colors
                ${index === selectedIndex ? "bg-[#fdf5ec] border-[#C97A3C]/20" : ""}`}
            >
              <div className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {prediction.structured_formatting.main_text}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {prediction.structured_formatting.secondary_text}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
